import { ChevronRight, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router";
import type { TBrandIcon } from "~/components/general/BrandIcons";

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "~/components/ui/collapsible";
import {
	SidebarGroup,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from "~/components/ui/sidebar";

export function NavMain({
	items,
}: {
	items: {
		title: string;
		url: string;
		icon?: LucideIcon | TBrandIcon;
		isActive?: boolean;
		items?: {
			title: string;
			url: string;
		}[];
	}[];
}) {
	const location = useLocation();
	// A link whose URL is a prefix of another link's (a section home such as
	// /borrower or /servicer) must match exactly, or it would light up on
	// every page beneath it.
	const isCurrent = (url: string) => {
		const isSectionHome = items.some(
			(other) => other.url !== url && other.url.startsWith(`${url}/`),
		);
		return isSectionHome
			? location.pathname === url
			: location.pathname.startsWith(url);
	};
	return (
		<SidebarGroup>
			<SidebarMenu>
				{items.map((item) =>
					item.items ? (
						<Collapsible
							asChild
							className="group/collapsible"
							defaultOpen={item.isActive}
							key={item.title}
						>
							<SidebarMenuItem>
								<CollapsibleTrigger asChild>
									<SidebarMenuButton tooltip={item.title}>
										{item.icon && <item.icon />}
										<span>{item.title}</span>
										<ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
									</SidebarMenuButton>
								</CollapsibleTrigger>
								<CollapsibleContent>
									<SidebarMenuSub>
										{item.items.map((subItem) => (
											<SidebarMenuSubItem key={subItem.title}>
												<SidebarMenuSubButton asChild>
													<Link to={subItem.url}>
														<span>{subItem.title}</span>
													</Link>
												</SidebarMenuSubButton>
											</SidebarMenuSubItem>
										))}
									</SidebarMenuSub>
								</CollapsibleContent>
							</SidebarMenuItem>
						</Collapsible>
					) : (
						<SidebarMenuItem key={item.title}>
							<SidebarMenuButton
								asChild
								isActive={isCurrent(item.url)}
								tooltip={item.title}
							>
								<Link to={item.url}>
									{item.icon && <item.icon />}
									<span>{item.title}</span>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					),
				)}
			</SidebarMenu>
		</SidebarGroup>
	);
}
